import CardHeader from "components/CardHeader";
import { Account } from "models/Account";
import { Project, ProjectHourEntry, ProjectWorker } from "models/Project";
import React, { useMemo, useState } from "react";
import { Badge, Button, Card, CardBody, Spinner, Table } from "reactstrap";
import { Link } from "react-router-dom";
import { useOfficeTranslation } from "i18n/OfficeTranslation";

interface Props {
    project: Project;
    accountById: Map<string, Account>;
    onRefresh: () => Promise<void>;
}

const dateKey = (value: Date) => {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};
const shiftDays = (value: Date, days: number) => {
    const result = new Date(value);
    result.setDate(result.getDate() + days);
    return result;
};
const mondayFor = (value: Date) => shiftDays(value, -((value.getDay() + 6) % 7));
const hoursLabel = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const remainder = minutes % 60;
    return `${hours ? `${hours}u` : ""}${hours && remainder ? " " : ""}${remainder ? `${remainder}m` : ""}`;
};
const entriesForWeek = (worker: ProjectWorker, weekStart: Date) => {
    const start = dateKey(weekStart);
    const end = dateKey(shiftDays(weekStart, 7));
    return (worker.entries ?? []).filter(entry => {
        const date = entry.date.slice(0, 10);
        return date >= start && date < end;
    });
};
const minutesFor = (entries: ProjectHourEntry[]) =>
    entries.reduce((total, entry) => total + entry.minutes, 0);

const ProjectHoursOverview: React.FC<Props> = ({ project, accountById, onRefresh }) => {
    const { intlLocale, t } = useOfficeTranslation();
    const today = useMemo(() => new Date(), []);
    const [weekStart, setWeekStart] = useState(() => mondayFor(today));
    const [refreshing, setRefreshing] = useState(false);
    const days = useMemo(
        () => Array.from({ length: 7 }, (_, index) => shiftDays(weekStart, index)),
        [weekStart]
    );
    const weekEnd = days[6];
    const projectStarts = project.start_date.slice(0, 10);
    const projectEnds = project.end_date.slice(0, 10);
    const weekOverlapsProject = dateKey(weekEnd) >= projectStarts && dateKey(weekStart) <= projectEnds;
    const weekHasStarted = dateKey(weekStart) <= dateKey(today);
    const canBeMissing = weekOverlapsProject && weekHasStarted;
    const workers = useMemo(() => [...project.workers].sort((first, second) => {
        const firstName = accountById.get(first.account_id)?.legal_name ?? first.account_id;
        const secondName = accountById.get(second.account_id)?.legal_name ?? second.account_id;
        return firstName.localeCompare(secondName, "nl");
    }), [project.workers, accountById]);
    const workerWeeks = workers.map(worker => {
        const entries = entriesForWeek(worker, weekStart);
        return { worker, entries, total: minutesFor(entries) };
    });
    const missingCount = canBeMissing
        ? workerWeeks.filter(workerWeek => workerWeek.total === 0).length
        : 0;
    const entryCount = workerWeeks.reduce(
        (total, workerWeek) => total + workerWeek.entries.length,
        0,
    );
    const weekTotal = workerWeeks.reduce((total, workerWeek) => total + workerWeek.total, 0);

    const refresh = async () => {
        setRefreshing(true);
        try {
            await onRefresh();
        } finally {
            setRefreshing(false);
        }
    };

    return <Card className="project-hours-overview mt-4">
        <CardBody>
            <CardHeader
                title={t("projects.projectHoursOverview.title")}
                icon="mdi-calendar-clock"
                count={entryCount}
                actions={<Button color="light" size="sm" disabled={refreshing} onClick={refresh}>
                    {refreshing ? <Spinner size="sm" /> : <i className="mdi mdi-refresh" />}
                    <span className="ms-1">{t("projects.projectHoursOverview.refresh")}</span>
                </Button>}
            />

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 py-3 border-bottom">
                <div>
                    {missingCount > 0 && <Badge color="danger" pill className="mb-2">
                        {missingCount} {t(missingCount === 1 ? "projects.hours.workerWithoutHours" : "projects.hours.workersWithoutHours")}
                    </Badge>}
                    <p className="text-muted mb-0">
                        {t("projects.projectHoursOverview.allWorkersRemainVisibleEvenWhenTheyHaveNotEnteredAnythingThisWeek")}
                    </p>
                </div>
                <div className="text-end">
                    <strong>{hoursLabel(weekTotal) || t("common.duration.zeroHoursShort")}</strong>
                    <small className="text-muted d-block">{t("projects.projectHoursOverview.totalThisWeek")}</small>
                </div>
            </div>

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 py-3 border-bottom mb-3">
                <Button color="light" size="sm" onClick={() => setWeekStart(shiftDays(weekStart, -7))}>
                    <i className="mdi mdi-chevron-left me-1" />{t("projects.projectHoursOverview.previousWeek")}
                </Button>
                <div className="text-center">
                    <strong>
                        {new Intl.DateTimeFormat(intlLocale, { day: "numeric", month: "long" }).format(days[0])}
                        {" – "}
                        {new Intl.DateTimeFormat(intlLocale, {
                            day: "numeric", month: "long", year: "numeric"
                        }).format(days[6])}
                    </strong>
                    {!weekOverlapsProject && <small className="text-muted d-block">{t("projects.projectHoursOverview.outsideTheProjectPeriod")}</small>}
                    {weekOverlapsProject && !weekHasStarted && <small className="text-muted d-block">{t("projects.projectHoursOverview.thisWeekHasNotStartedYet")}</small>}
                </div>
                <div className="d-flex gap-2">
                    <Button color="light" size="sm" onClick={() => setWeekStart(mondayFor(today))}>{t("projects.projectHoursOverview.thisWeek")}</Button>
                    <Button color="light" size="sm" onClick={() => setWeekStart(shiftDays(weekStart, 7))}>
                        {t("projects.projectHoursOverview.nextWeek")}<i className="mdi mdi-chevron-right ms-1" />
                    </Button>
                </div>
            </div>

            <div className="table-responsive">
                <Table bordered hover className="project-hours-overview__table align-middle mb-0">
                    <thead>
                        <tr>
                            <th className="project-hours-overview__worker">{t("common.worker")}</th>
                            {days.map(day => <th key={dateKey(day)}
                                className={dateKey(day) === dateKey(today) ? "bg-primary bg-soft" : ""}>
                                <span className="text-uppercase small text-muted">
                                    {new Intl.DateTimeFormat(intlLocale, { weekday: "short" }).format(day)}
                                </span>
                                <strong className="d-block">{day.getDate()}</strong>
                            </th>)}
                            <th>{t("projects.projectHoursOverview.total")}</th>
                            <th>{t("common.status")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {workerWeeks.map(({ worker, entries, total }) => {
                            const account = accountById.get(worker.account_id);
                            const pending = entries.filter(entry => entry.status === "pending_approval").length;
                            const missing = canBeMissing && total === 0;
                            return <tr key={worker.account_id} className={missing ? "project-hours-overview__missing" : ""}>
                                <td className="project-hours-overview__worker">
                                    <Link to={`/accounts/${worker.account_id}`} className="text-body fw-semibold">
                                        {account?.legal_name ?? worker.account_id}
                                    </Link>
                                    {worker.role && <small className="text-muted d-block">{worker.role}</small>}
                                </td>
                                {days.map(day => {
                                    const dayKey = dateKey(day);
                                    const dayEntries = entries.filter(entry => entry.date.slice(0, 10) === dayKey);
                                    const minutes = minutesFor(dayEntries);
                                    const pendingToday = dayEntries.some(entry => entry.status === "pending_approval");
                                    const descriptions = dayEntries
                                        .map(entry => entry.description)
                                        .filter(Boolean)
                                        .join("\n");
                                    return <td key={dayKey}
                                        className={dayKey > dateKey(today) ? "project-hours-overview__future" : ""}
                                        title={descriptions}>
                                        {minutes > 0 ? <>
                                            <strong>{hoursLabel(minutes)}</strong>
                                            <small className={`d-block ${pendingToday ? "text-warning" : "text-success"}`}>
                                                {t(pendingToday ? "projects.hours.pending" : "projects.hours.approved")}
                                            </small>
                                        </> : <span className="text-muted">—</span>}
                                    </td>;
                                })}
                                <td><strong>{hoursLabel(total) || t("common.duration.zeroHoursShort")}</strong></td>
                                <td>
                                    {missing ? <Badge color="danger">{t("projects.projectHoursOverview.noHoursEntered")}</Badge> :
                                        !weekOverlapsProject ? <Badge color="secondary">{t("projects.projectHoursOverview.outsideProjectPeriod")}</Badge> :
                                            !weekHasStarted ? <Badge color="secondary">{t("projects.projectHoursOverview.notStartedYet")}</Badge> :
                                                pending > 0 ? <Badge color="warning">{pending} {t("projects.projectHoursOverview.pending")}</Badge> :
                                                    total > 0 ? <Badge color="success">{t("projects.projectHoursOverview.hoursEntered")}</Badge> :
                                                        <Badge color="secondary">{t("projects.projectHoursOverview.noHours")}</Badge>}
                                </td>
                            </tr>;
                        })}
                        {workers.length === 0 && <tr>
                            <td colSpan={10} className="text-muted text-center py-5">{t("projects.projectHoursOverview.noWorkersLinkedYet")}</td>
                        </tr>}
                    </tbody>
                </Table>
            </div>
            <div className="d-flex flex-wrap gap-3 mt-3 small text-muted">
                <span><i className="mdi mdi-circle text-danger me-1" />{t("projects.projectHoursOverview.noHoursEntered")}</span>
                <span><i className="mdi mdi-circle text-warning me-1" />{t("projects.projectHoursOverview.pendingApproval")}</span>
                <span><i className="mdi mdi-circle text-success me-1" />{t("projects.projectHoursOverview.approved")}</span>
            </div>
        </CardBody>
    </Card>;
};

export default ProjectHoursOverview;
